import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type AppLayoutOverride = boolean

type AppLayoutOverrideContextValue = {
  override: AppLayoutOverride
  setOverride: (value: AppLayoutOverride) => void
}

export const PRODUCT_REFRESH_PREVIEW_STORAGE_KEY = 'calendly_publisher_product_refresh_preview'

const STORAGE_KEY = PRODUCT_REFRESH_PREVIEW_STORAGE_KEY

const noop = () => {}

const defaultContextValue: AppLayoutOverrideContextValue = {
  override: false,
  setOverride: noop,
}

const AppLayoutOverrideContext = createContext<AppLayoutOverrideContextValue>(defaultContextValue)

/** Removes the dev preview override from localStorage (e.g. brand refresh debug Clear). */
export function clearProductRefreshPreviewFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // localStorage may be unavailable
  }
}

function readFromStorage(): AppLayoutOverride {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'true') return true
    if (stored === 'false') return false
  } catch {
    // localStorage may be unavailable (e.g. private browsing with strict settings)
  }
  return false
}

/**
 * Provides the product refresh preview override state to the app.
 * Mount once near the app root. The experience defaults to off (`false`); this override is
 * only for internal preview when dev flags are visible. Persists to localStorage and syncs across tabs.
 */
export function AppLayoutOverrideProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<AppLayoutOverride>(readFromStorage)
  const setOverride = useCallback((value: AppLayoutOverride) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(value))
    } catch {
      // ignore write failures
    }
    setOverrideState(value)
  }, [])

  // Sync changes made in other tabs
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return
      const value = event.newValue
      if (value === 'true') setOverrideState(true)
      else if (value === 'false') setOverrideState(false)
      else setOverrideState(false)
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const value = useMemo(
    () => ({ override, setOverride }),
    [override, setOverride]
  )

  return (
    <AppLayoutOverrideContext.Provider value={value}>
      {children}
    </AppLayoutOverrideContext.Provider>
  )
}

/** Shared preview gate for product refresh shell + UI theme; used only by product-refresh hooks. */
export function useProductRefreshPreviewContext(): AppLayoutOverrideContextValue {
  return useContext(AppLayoutOverrideContext)
}
